// File: frontend/src/utils/analytics.ts
// src/utils/analytics.ts
// Google Analytics 4 — native gtag wrapper (no external library needed)
// Uses REACT_APP_GA_MEASUREMENT_ID env variable so QA and PROD can have separate streams.
//
// Consent Mode v2:
//   Previously the gtag scripts were injected only after the visitor accepted
//   measurement cookies, which meant every visitor who ignored the bar was
//   invisible in GA — including organic search arrivals we could see in Search
//   Console but never in Analytics.
//
//   The library is now loaded on every visit, but it starts in the denied
//   state declared in public/index.html: no cookies are written, no device
//   identifier is created, and only cookieless pings are sent. Those pings let
//   Google report traffic volume and source without identifying anyone.
//
//   When the visitor accepts, CookieConsentContext calls applyConsent() and the
//   state flips to granted, at which point normal measurement begins.
//   Withdrawing consent flips it back and storage is dropped again.
//
//   IMPORTANT: the Cookie Policy text must describe this behaviour. The old
//   wording ("measurement never starts before you consent") is no longer
//   accurate and has to be replaced with a description of cookieless pings.

declare global {
  interface Window {
    gtag: (...args: unknown[]) => void;
    dataLayer: unknown[];
  }
}

const GA_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;

/**
 * Mirror of the visitor's measurement decision.
 * Kept only for debugging and tests — it no longer gates sending, because
 * under Consent Mode the denied state is handled inside gtag itself.
 */
let analyticsGranted = false;

/**
 * Ensure a gtag() function exists even before the remote library has loaded.
 * public/index.html already defines it, but a direct unit test or a stripped
 * HTML shell might not, and a missing gtag would throw on the first call.
 */
const ensureGtagStub = (): void => {
  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag !== "function") {
    window.gtag = function gtag() {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer.push(arguments);
    };
  }
};

/**
 * Inject the gtag <script> tags into <head> at runtime.
 * Called once at application startup, regardless of consent — the denied
 * default set in public/index.html is what keeps this lawful before a choice
 * is made. Does nothing if GA_ID is not set.
 */
export const initAnalytics = (): void => {
  if (!GA_ID) {
    console.warn("[Analytics] REACT_APP_GA_MEASUREMENT_ID is not set — GA disabled.");
    return;
  }

  ensureGtagStub();

  // Avoid double-injection if called multiple times
  if (document.getElementById("ga-script")) return;

  // 1) Load the gtag.js library
  const scriptSrc = document.createElement("script");
  scriptSrc.id = "ga-script";
  scriptSrc.async = true;
  scriptSrc.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(scriptSrc);

  // 2) Initialize the stream.
  //    send_page_view stays false because App.tsx fires page_view itself on
  //    every route change — the SPA would otherwise only ever report the
  //    landing URL.
  window.gtag("js", new Date());
  window.gtag("config", GA_ID, { send_page_view: false });
};

/**
 * Apply a consent decision to the running tag.
 *
 * Granting switches analytics storage on, so GA starts writing its client-id
 * cookie and reporting this visitor normally. Denying (or withdrawing) switches
 * it back off: gtag keeps sending cookieless pings, which Google counts toward
 * traffic totals without storing anything on the device.
 *
 * ad_user_data and ad_personalization are the two signals Consent Mode v2 added
 * on top of ad_storage. They are required — omitting them makes Google treat the
 * whole consent state as incomplete.
 *
 * @param granted measurement (GA4) consent
 * @param adsGranted advertising consent; the platform runs no ads today, but the
 *                   preference exists in the banner, so it is honoured here too
 */
export const applyConsent = (granted: boolean, adsGranted: boolean): void => {
  analyticsGranted = granted;

  ensureGtagStub();

  window.gtag("consent", "update", {
    analytics_storage: granted ? "granted" : "denied",
    ad_storage: adsGranted ? "granted" : "denied",
    ad_user_data: adsGranted ? "granted" : "denied",
    ad_personalization: adsGranted ? "granted" : "denied",
  });
};

/**
 * Backwards-compatible alias for the previous single-argument API.
 * Advertising consent is assumed denied, which is the safe default.
 */
export const applyAnalyticsConsent = (granted: boolean): void =>
  applyConsent(granted, false);

/** Whether measurement is currently allowed — exported for tests and debugging. */
export const isAnalyticsGranted = (): boolean => analyticsGranted;

/**
 * Send a page_view event to GA4.
 * Call this on every route change.
 *
 * No consent check here on purpose: under Consent Mode the denied state is
 * enforced inside gtag, which downgrades the hit to a cookieless ping instead
 * of dropping it. Skipping the call would throw away exactly the traffic data
 * this change exists to recover.
 *
 * @param path  - e.g. "/galatasaray-vs-besiktas-maci--3"
 * @param title - document.title or a custom label
 */
export const trackPageView = (path: string, title?: string): void => {
  if (!GA_ID || typeof window.gtag !== "function") return;

  window.gtag("event", "page_view", {
    page_path: path,
    page_title: title || document.title,
    // GA4 uses this to identify the stream
    send_to: GA_ID,
  });
};

/**
 * Send a custom event to GA4.
 * Example: trackEvent("entry_posted", { match_id: 42 })
 *
 * Same reasoning as trackPageView: gtag decides what a denied visitor's hit
 * may contain, so this function does not second-guess it.
 */
export const trackEvent = (
  eventName: string,
  params?: Record<string, unknown>
): void => {
  if (!GA_ID || typeof window.gtag !== "function") return;

  window.gtag("event", eventName, {
    ...params,
    send_to: GA_ID,
  });
};